import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { InvoiceStatusBadge } from '@/components/InvoiceStatusBadge'
import { HistoryRowTag } from '@/components/history/HistoryRowTag'
import { formatCurrency, formatDate } from '@/lib/format'
import { encodeLink, HISTORY_INVOICE_STATUS_LABELS } from '@/lib/history'
import type { LinkOption } from '@/lib/history'
import type { HistoryInvoice, HistoryInvoiceLink, Invoice } from '@/api/types'

export type InvoiceLine =
  | { kind: 'platform'; invoice: Invoice }
  | { kind: 'add'; index: number; invoice: HistoryInvoice; options: LinkOption[] }

interface HistoryInvoiceTableRowProps {
  line: InvoiceLine
  isDisabled: boolean
  onLinkChange: (index: number, link: HistoryInvoiceLink) => void
  onWhyChange: (index: number, why: string) => void
  onRemove: (index: number) => void
}

export function HistoryInvoiceTableRow({
  line,
  isDisabled,
  onLinkChange,
  onWhyChange,
  onRemove
}: HistoryInvoiceTableRowProps): React.ReactElement {
  if (line.kind === 'platform') {
    const { invoice } = line
    const linked = invoice.shipment_id === null ? 'utan leverans' : `leverans på rad ${invoice.shipment_id}`

    return (
      <tr className="border-t border-border/60">
        <td className="px-3 py-2">
          <HistoryRowTag kind="keep" />
        </td>
        <td className="px-3 py-2 font-medium">
          <Link
            to={`/invoices/${invoice.id}`}
            className="hover:underline"
          >
            {invoice.invoice_number ?? 'utan nummer'}
          </Link>
        </td>
        <td className="px-3 py-2">{invoice.invoice_date ? formatDate(invoice.invoice_date) : '–'}</td>
        <td className="px-3 py-2 tabular-nums">{formatCurrency(Number(invoice.amount))}</td>
        <td className="px-3 py-2">
          <InvoiceStatusBadge status={invoice.status} />
        </td>
        <td className="px-3 py-2 text-muted-foreground">{linked}</td>
        <td className="px-3 py-2" />
      </tr>
    )
  }

  const { invoice, index, options } = line
  const isFetched = invoice.guid !== ''
  const isExternal = invoice.link.kind === 'external'

  const handleLinkChange = (value: string): void => {
    const option = options.find((candidate) => candidate.value === value)

    if (option) onLinkChange(index, option.link)
  }

  return (
    <tr className="border-t border-border/60 bg-emerald-50/40 align-top">
      <td className="px-3 py-2">
        <HistoryRowTag kind="add" />
      </td>
      <td className="px-3 py-2 font-medium">{invoice.number}</td>
      <td className="px-3 py-2">{isFetched ? formatDate(invoice.date) : '–'}</td>
      <td className="px-3 py-2 tabular-nums">{isFetched ? formatCurrency(invoice.amount) : 'hämtas'}</td>
      <td className="px-3 py-2 text-xs text-muted-foreground">
        {isFetched && `${HISTORY_INVOICE_STATUS_LABELS[invoice.status]}, betalt ${formatCurrency(invoice.paid)}`}
        {!isFetched && 'från Capway vid förhandsgranskningen'}
      </td>
      <td className="px-3 py-2">
        <div className="space-y-1">
          <Select
            value={encodeLink(invoice.link)}
            disabled={isDisabled}
            onValueChange={handleLinkChange}
          >
            <SelectTrigger className="w-72">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isExternal && (
            <Input
              value={invoice.link.kind === 'external' ? invoice.link.why : ''}
              disabled={isDisabled}
              onChange={(event) => onWhyChange(index, event.target.value)}
              placeholder="Vad fakturan gällde"
              aria-label="Vad fakturan gällde"
              className="w-72"
            />
          )}
        </div>
      </td>
      <td className="px-3 py-2 text-right">
        <Button
          variant="ghost"
          size="sm"
          className="rounded-none text-destructive hover:text-destructive"
          disabled={isDisabled}
          onClick={() => onRemove(index)}
        >
          Ta bort raden
        </Button>
      </td>
    </tr>
  )
}
